import { AnyObject } from '@store/types';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';

import {
    EhrChatData,
    EhrDataListItem,
    EhrSearchQuery,
    InformedConsentStatus,
    ReferralStatus,
} from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { ValueType } from '@store/abstract/Form';
import { serviceService } from '@services/services/serviceService';
import { EhrApi } from '@api/EhrApi';
import { InformedConsentApi } from '@api/InformedConsentApi';
import { LastViewApi } from '@api/LastViewApi';
import { ReferralApi } from '@api/ReferralApi';
import { RepresentativeApi } from '@api/RepresentativeApi';
import { getUniqNewIds } from '@utils/getUniqNewIds';
import { getUniqIds } from '@utils/getUniqIds';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { loadWithChunkAndFlatten } from '@utils/loadWithChunkAndFlatten';
import { translate } from '@chaika/i18n/helper';
import { EhrUnpaidRenderedServiceApi } from '@api/EhrUnpaidRenderedServiceApi';
import { isDefined } from '@utils/isDefined';
import { SavedPaymentMethodApi } from '@api';
import { SavedPaymentMethodStatus } from 'chaika-enums';

const findApi = AwesomeDebouncePromise(EhrApi.find, 500);

interface LoadInformedConsentForReservationData {
    patientId: string;
    reservationId: string;
    serviceId: string;
}

class PatientService {
    private loadedIds = new Set<string>();
    public async load(withoutMergedEhrs = false): Promise<void> {
        const patientList = store.patientStore.patientList;

        const [patients] = await Promise.all<any>([EhrApi.find({ limit: 50 })]);

        patientList.set(patients);
    }

    public async loadSimilar(ehrId: string): Promise<void> {
        const { patientStore } = store;

        if (!patientStore.isSimilarEhrsLoaded(ehrId)) {
            const similar = await EhrApi.findSimilar(ehrId);

            patientStore.setSimilarEhrsLoaded(ehrId, similar);
        }
    }

    public async loadSimilarByIds(ehrIds: string[]): Promise<void> {
        const { patientStore } = store;

        const notLoadedIds = ehrIds.filter(ehrId => !patientStore.isSimilarEhrsLoaded(ehrId));

        if (notLoadedIds.length > 0) {
            const chunkIds = getUniqChunkedIds(notLoadedIds);
            const result = await Promise.all(chunkIds.map(ids => EhrApi.findSimilarForEhrs(ids)));

            result.forEach(similar => {
                Object.keys(similar).forEach(ehrId => {
                    patientStore.setSimilarEhrsLoaded(ehrId, similar[ehrId]?.ehrs || []);
                });
            });
        }
    }

    public async loadRepresentative(ehrId: string): Promise<void> {
        const patient = store.patientStore.patientList.getExisting(ehrId);
        if (!patient.representativeList.isDataSet) {
            const representatives = await RepresentativeApi.getEhrRepresentativeList(ehrId);
            const representativeIds = representatives.map(({ representativeId }) => representativeId);

            await this.loadByIds(representativeIds);

            patient.representativeList.set(representatives);
        }
    }

    public async loadCards(ehrId: string): Promise<void> {
        await this.loadByIds([ehrId]);
        const patient = store.patientStore.patientList.getExisting(ehrId);
        if (!patient.isCardLoaded) {
            patient.cards = await SavedPaymentMethodApi.find({
                userId: ehrId,
                status: SavedPaymentMethodStatus.Active,
            });
        }
    }

    public async loadLastViewPatients(userId: string): Promise<void> {
        const patientList = store.patientStore.lastViewPatientList;

        const [patients] = await Promise.all<any>([LastViewApi.getEhrList(userId)]);

        patientList.set(patients);
    }

    public async loadPatientActiveReferrals(ehrId: string): Promise<void> {
        const patientList = store.patientStore.patientList;
        await this.loadByIds([ehrId]);

        const patient = patientList.getExisting(ehrId);

        const referrals = await ReferralApi.getList({ ehrId, statuses: ReferralStatus.Active });
        await serviceService.loadByIds(referrals.map(({ serviceId }) => serviceId));
        patient.referralList.set(referrals);
    }

    public async loadPatientsUnpaidRenderedServiceCount(ehrIds: string[]): Promise<void> {
        const patientList = store.patientStore.patientList;

        const uniqIds = getUniqIds(ehrIds);
        if (uniqIds.length > 0) {
            await this.loadByIds(uniqIds);

            const notLoadedIds = ehrIds.filter(
                id => patientList.has(id) && !patientList.getExisting(id).isUnpaidRenderedServiceLoaded,
            );

            await this.setUnpaidRenderedServiceCount(notLoadedIds);
        }
    }

    public async loadPatientReferrals(ehrId: string): Promise<void> {
        const patientList = store.patientStore.patientList;
        await this.loadByIds([ehrId]);

        const patient = patientList.getExisting(ehrId);

        const referrals = await ReferralApi.getList({ ehrId });
        await serviceService.loadByIds(referrals.map(({ serviceId }) => serviceId));
        patient.referralList.set(referrals);
    }

    public async loadInformedConsentForReservation({
        patientId,
        reservationId,
        serviceId,
    }: LoadInformedConsentForReservationData): Promise<void> {
        const { patientList } = store.patientStore;
        const mainId = patientList.getMainId(patientId);
        const patient = store.patientStore.patientList.getExisting(mainId);
        if (!patient.hasInformedConsent(reservationId, serviceId)) {
            const informedConsents = await InformedConsentApi.getList({ reservationId, ehrId: patientId });
            const informedConsent =
                informedConsents.length > 0
                    ? (informedConsents.find(({ status }) => status === InformedConsentStatus.Signed) ??
                      informedConsents[0])
                    : null;
            patient.setInformedConsent(reservationId, serviceId, informedConsent);
        }
    }

    public async loadInformedConsentForReservations(params: LoadInformedConsentForReservationData[]): Promise<void> {
        const { patientList } = store.patientStore;

        const notLoaded: LoadInformedConsentForReservationData[] = [];
        const reservationIds: string[] = [];

        params.forEach(({ patientId, reservationId, serviceId }) => {
            const mainId = patientList.getMainId(patientId);
            const patient = store.patientStore.patientList.getExisting(mainId);
            if (!patient.hasInformedConsent(reservationId, serviceId)) {
                notLoaded.push({ patientId, reservationId, serviceId });
                reservationIds.push(reservationId);
            }
        });

        if (notLoaded.length > 0) {
            const informedConsents = await loadWithChunkAndFlatten(
                reservationIds,
                InformedConsentApi.getList,
                'reservationId',
            );

            const informedConsentsObject = groupBy(
                informedConsents,
                ({ ehrId, reservationId }) => `${ehrId}:${reservationId}`,
            );

            notLoaded.forEach(({ patientId, reservationId, serviceId }) => {
                const mainId = patientList.getMainId(patientId);
                const patient = store.patientStore.patientList.getExisting(mainId);

                const patientInformedConsents = informedConsentsObject[`${patientId}:${reservationId}`] || [];

                const filtered = patientInformedConsents.filter(({ risks }) =>
                    risks.some(risk => risk.serviceId === serviceId),
                );

                const informedConsent =
                    filtered.length > 0
                        ? (filtered.find(({ status }) => status === InformedConsentStatus.Signed) ?? filtered[0])
                        : null;

                patient.setInformedConsent(reservationId, serviceId, informedConsent);
            });
        }
    }

    public reset(): void {
        const patientStore = store.patientStore;

        patientStore.searchList.reset();
        patientStore.patientList.reset();
    }

    public resetSearchList(): void {
        store.patientStore.searchList.reset();
    }

    public async reloadSearchList() {
        const { searchList } = store.patientStore;
        const patients = await findApi(searchList.searchParams);
        searchList.setResult(patients, patients.length);
    }

    public async loadByIds(ids: string[], ignoreLoadedIds?: boolean): Promise<void> {
        const {
            patientStore: { patientList },
        } = store;

        const newIds = getUniqNewIds(
            [...patientList.ids, ...(ignoreLoadedIds ? [] : toArrayFromIterable(this.loadedIds))],
            ids.map(id => patientList.getMainId(id)),
        );
        for (const id of newIds) {
            this.loadedIds.add(id);
        }

        await this.reloadByIds(newIds);
    }

    public async reloadByIds(ehrIds: string[]): Promise<void> {
        const patientList = store.patientStore.patientList;

        if (ehrIds.length > 0) {
            const patients = await loadWithChunkAndFlatten(ehrIds, EhrApi.find);
            patientList.add(patients);
        }
    }

    public async loadPatientEntryCountByEhrIds(ehrIds: string[]): Promise<void> {
        const patientList = store.patientStore.patientList;
        const loadedIds = new Set(
            patientList.values.filter(({ entryCount }) => !isNil(entryCount)).map(({ id }) => id),
        );
        const notLoadedIds = ehrIds.filter(id => !loadedIds.has(id));

        if (notLoadedIds.length > 0) {
            const uniqIds = uniq(notLoadedIds);
            const chunkIds = getUniqChunkedIds(uniqIds);
            const ehrsEntryCount = flatten(await Promise.all(chunkIds.map(ids => EhrApi.getEhrsEntryCount(ids))));
            await this.loadByIds(uniqIds);

            const countObject = keyBy(flatten(ehrsEntryCount), 'ehrId');

            notLoadedIds.forEach(id => {
                const patient = patientList.get(id);
                if (isDefined(patient)) {
                    patient.entryCount = countObject[id]?.count ?? 0;
                } else {
                    console.error('NOT FOUND PATIENT', id);
                }
            });
        }
    }

    public async find(params: EhrSearchQuery): Promise<EhrDataListItem[]> {
        return findApi(params);
    }

    public async loadTgChatLinks(id: string, doctorId?: string): Promise<EhrChatData[]> {
        const { patientStore } = store;

        const [{ chats }] = await Promise.all([EhrApi.findChat(id, { doctorId }), this.loadByIds([id])]);

        const patient = patientStore.patientList.get(id);
        patient?.setTgChatLinks(chats);

        return chats;
    }

    public async loadTgChatLinksWithReloading(id: string, doctorId: string): Promise<void> {
        const [{ chats }] = await Promise.all([EhrApi.findChat(id), this.loadByIds([id])]);

        const patient = store.patientStore.patientList.get(id);
        patient?.setTgChatLinks(chats);

        let tryCount = 0;
        const updaterId = setInterval(async () => {
            const chatWithDoctor = patient?.tgChatLinks?.find(chat => chat.doctorId === doctorId);

            if (isDefined(chatWithDoctor) || tryCount >= 15) {
                clearInterval(updaterId);
            } else {
                const { chats } = await EhrApi.findChat(id);
                patient?.setTgChatLinks(chats);
                tryCount += 1;
            }
        }, 3000);
    }

    public async findAndAdd(params: EhrSearchQuery): Promise<EhrDataListItem[]> {
        const patients = await findApi(params);

        store.patientStore.patientList.addIfNotExist(patients);

        return patients;
    }

    public async findWitIdsFilter(params: EhrSearchQuery, ids: string[]): Promise<EhrDataListItem[]> {
        const chunked = getUniqChunkedIds(ids, 300);
        const result =
            chunked.length > 0
                ? await Promise.all(chunked.map(ids => EhrApi.find({ ...params, ids: ids.join(',') })))
                : await EhrApi.find(params);
        return flatten(result);
    }

    public async updateFieldValue(patientId: string, name: string, value: ValueType) {
        const patient: AnyObject = store.patientStore.patientList.getExisting(patientId);
        const currentValue = patient[name];

        try {
            await EhrApi.update(patientId, { [name]: value });
            patient[name] = value;
        } catch (e: any) {
            console.error(e);
            store.app.showingPopupParams = {
                title: translate('errorOnSaveData'),
                description: e.name === 'Conflict' ? translate('ehrHasDuplicateCantChange') : e.message,
            };
            patient[name] = currentValue;
        }
    }

    public getActualPatientIds(ids: string[]): string[] {
        const {
            patientStore: { patientList },
        } = store;

        return uniq(ids.map(id => patientList.getExisting(id).id));
    }

    private async setUnpaidRenderedServiceCount(ehrIds: string[]): Promise<void> {
        const patientList = store.patientStore.patientList;

        const chunkIds = getUniqChunkedIds(ehrIds);
        const countResponse = flatten(
            await Promise.all(chunkIds.map(ids => EhrUnpaidRenderedServiceApi.getEhrCount(ids))),
        );

        countResponse.forEach(({ ehrId, count }) => {
            const patient = patientList.get(ehrId);

            if (isDefined(patient)) {
                patient.unpaidRenderedServiceCount = (patient.unpaidRenderedServiceCount ?? 0) + count;
            } else {
                console.error('NOT FOUND PATIENT', ehrId);
            }
        });
    }
}

export const patientService = new PatientService();
